<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useNow } from "@vueuse/core";
import { useForm } from "vee-validate";
import { computed, watch } from "vue";
import z from "zod";

import {
  IProgrammingDateRangeInputsExpose,
  TProgrammingDateRangeInputs,
} from "@/components/piloting/planning/programming-range/date-range-inputs/programming-date-range-inputs.types";
import { isProgrammingRangeStartDateAllowed } from "@/components/piloting/planning/programming-range/date-range-inputs/programming-date-range-inputs.utils";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { ProgrammingRange } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import { useTypedIntl } from "@/locales/typed-intl";
import $date, { EDateScaling } from "@/utils/date";

import DatePicker from "primevue/datepicker";
import ToggleSwitch from "primevue/toggleswitch";

const props = defineProps<{
  initialDateRange?: Pick<ProgrammingRange, "start" | "end">;
  disabled?: boolean;
}>();
const emits = defineEmits<{
  (e: "submit-edit", payload: Partial<TProgrammingDateRangeInputs>): void;
  (e: "submit-create", payload: TProgrammingDateRangeInputs): void;
}>();

const { t, k, rawT } = useTypedIntl();

const now = useNow({ interval: 60_000 });
const startDateSchema = z
  .date()
  .refine(
    (startDate) =>
      isProgrammingRangeStartDateAllowed(
        startDate,
        $date.subtract(now.value, 30, EDateScaling.MINUTE),
        props.initialDateRange?.start,
      ),
    "errors.time.startNotPassed",
  );

const definiteDateRangeSchema = z.object({
  start: z.object({
    date: startDateSchema,
    time: startDateSchema,
  }),
  end: z.object({
    date: z.date(),
    time: z.date(),
  }),
  indefinitePeriod: z.literal<boolean>(false),
});

const indefiniteDateRangeSchema = z.object({
  start: z.object({
    date: startDateSchema,
    time: startDateSchema,
  }),
  end: z.literal(null),
  indefinitePeriod: z.literal<boolean>(true),
});

const dateRangeSchema = z
  .discriminatedUnion("indefinitePeriod", [definiteDateRangeSchema, indefiniteDateRangeSchema])
  .refine(
    ({ start, end }) => {
      if (end === null) return true;
      return (
        $date.getDateMilliseconds(end.time) >= $date.getDateMilliseconds($date.add(start.time, 50, EDateScaling.MINUTE))
      );
    },
    {
      message: "errors.programming_range.min_duration",
      path: ["end.time"],
    },
  );
type TFormDateRange = z.infer<typeof dateRangeSchema>;

function buildInitialValues(): TFormDateRange {
  const currentDate = now.value;

  if (!props.initialDateRange)
    return {
      start: {
        date: currentDate,
        time: currentDate,
      },
      end: {
        date: $date.add(currentDate, 1, EDateScaling.DAY),
        time: $date.add(currentDate, 1, EDateScaling.DAY),
      },
      indefinitePeriod: false,
    };

  const initialEnd = props.initialDateRange.end;

  return {
    start: {
      date: new Date(props.initialDateRange.start),
      time: new Date(props.initialDateRange.start),
    },
    end: initialEnd
      ? {
          date: new Date(initialEnd),
          time: new Date(initialEnd),
        }
      : null,
    indefinitePeriod: initialEnd ? false : true,
  };
}

const { errors, handleSubmit, resetForm, defineField, isFieldDirty, meta, setFieldValue } = useForm({
  validationSchema: toTypedSchema(dateRangeSchema),
  initialValues: buildInitialValues(),
});

const [startDate] = defineField("start.date");
const [startTime] = defineField("start.time");
const [endDate] = defineField("end.date");
const [endTime] = defineField("end.time");
const [indefinitePeriod] = defineField("indefinitePeriod");

const submit = handleSubmit((values) => {
  if (props.initialDateRange) {
    const changes: Partial<TProgrammingDateRangeInputs> = {
      start: isFieldDirty("start.time") ? $date.formatDateWithoutTimezone(values.start.time) : undefined,
      end: isFieldDirty("end") ? (values.end ? $date.formatDateWithoutTimezone(values.end.time) : null) : undefined,
    };

    emits("submit-edit", changes);
  } else {
    emits("submit-create", {
      start: $date.formatDateWithoutTimezone(values.start.time),
      end: values.end ? $date.formatDateWithoutTimezone(values.end.time) : null,
    });
  }
});

const minEndDate = computed<Date>(() => {
  if (!startDate.value) return now.value;

  return $date.add(startDate.value, 1, EDateScaling.HOUR);
});

const minEndTime = computed<Date>(() => {
  if (!startTime.value) return now.value;

  return $date.add(startTime.value, 1, EDateScaling.HOUR);
});

watch(startDate, (newValue) => {
  if (!newValue) return;

  if (startTime.value) {
    $date.updateDateOnly(startTime.value, newValue);

    if ($date.getDateMilliseconds(startTime.value) < $date.getDateMilliseconds(now.value)) {
      startTime.value = now.value;
    }
  }

  if (indefinitePeriod.value) return;

  if (
    endDate.value &&
    startDate.value &&
    $date.getDateMilliseconds(endDate.value) < $date.getDateMilliseconds(minEndDate.value)
  ) {
    endDate.value = $date.add(startDate.value, 1, EDateScaling.DAY);
  }

  if (
    endTime.value &&
    startTime.value &&
    $date.getDateMilliseconds(endTime.value) < $date.getDateMilliseconds(minEndTime.value)
  ) {
    endTime.value = minEndTime.value;
  }
});

watch(endDate, (newValue) => {
  if (!newValue) return;

  if (endTime.value) {
    $date.updateDateOnly(endTime.value, newValue);

    if (startTime.value && $date.getDateMilliseconds(endTime.value) < $date.getDateMilliseconds(startTime.value)) {
      endTime.value = $date.add(startTime.value, 1, EDateScaling.HOUR);
    }
  }
});

watch(startTime, () => {
  if (!startDate.value || !startTime.value) return;

  if (startDate.value.getDate() !== startTime.value.getDate()) {
    $date.updateDateOnly(startTime.value, startDate.value);
  }
});

watch(endTime, () => {
  if (!endDate.value || !endTime.value) return;

  if (endDate.value.getDate() !== endTime.value.getDate()) {
    $date.updateDateOnly(endTime.value, endDate.value);
  }
});

watch(indefinitePeriod, (newValue) => {
  if (newValue) {
    setFieldValue("end", null);
    return;
  }

  if (startDate.value) {
    endDate.value = $date.add(startDate.value, 1, EDateScaling.DAY);
    endTime.value = endDate.value;
  }
});

defineExpose<IProgrammingDateRangeInputsExpose>({
  submit,
  resetForm,
  isValid: () => meta.value.valid,
  isDirty: () => meta.value.dirty,
});
</script>

<template>
  <div
    class="grid grid-cols-2 gap-3"
    :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.component"
  >
    <div class="flex flex-col gap-2">
      <label
        class="text-text-secondary text-xs font-semibold"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startDate.label"
        >{{ t(k.control.piloting.planning.form.startDate) }}</label
      >
      <DatePicker
        v-model="startDate"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startDate.datePicker"
        :disabled="props.disabled"
        showIcon
        iconDisplay="input"
        :min-date="now"
        :invalid="!!errors['start.date']"
      />
      <small
        v-if="errors['start.date']"
        class="text-xs text-red-600"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startDate.errorText"
        >{{ rawT(`veeValidate.messages.${errors["start.date"]}`) }}</small
      >
    </div>
    <div class="flex flex-col gap-2">
      <label
        class="text-text-secondary text-xs font-semibold"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startTime.label"
        >{{ t(k.control.piloting.planning.form.startTime) }}</label
      >
      <DatePicker
        v-model="startTime"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startTime.datePicker"
        :disabled="props.disabled"
        showIcon
        iconDisplay="input"
        timeOnly
        :min-date="$date.startOf(now, EDateScaling.DAY)"
        :invalid="!!errors['start.time']"
      >
        <template #inputicon="slotProps">
          <i
            class="pi pi-clock"
            @click="slotProps.clickCallback"
          />
        </template>
      </DatePicker>
      <small
        v-if="errors['start.time']"
        class="text-xs text-red-600"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.startTime.errorText"
        >{{ rawT(`veeValidate.messages.${errors["start.time"]}`) }}</small
      >
    </div>
    <div class="flex flex-col gap-2">
      <label
        class="text-text-secondary text-xs font-semibold"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endDate.label"
        >{{ t(k.control.piloting.planning.form.endDate) }}</label
      >
      <DatePicker
        v-model="endDate"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endDate.datePicker"
        :disabled="props.disabled || indefinitePeriod"
        showIcon
        iconDisplay="input"
        :invalid="!!errors['end.date']"
        :min-date="minEndDate"
      />
      <small
        v-if="errors['end.date']"
        class="text-xs text-red-600"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endDate.errorText"
        >{{ rawT(`veeValidate.messages.${errors["end.date"]}`) }}</small
      >
    </div>
    <div class="flex flex-col gap-2">
      <label
        class="text-text-secondary text-xs font-semibold"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endTime.label"
        >{{ t(k.control.piloting.planning.form.endTime) }}</label
      >
      <DatePicker
        v-model="endTime"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endTime.datePicker"
        :disabled="props.disabled || indefinitePeriod"
        showIcon
        iconDisplay="input"
        timeOnly
        :invalid="!!errors['end.time']"
      >
        <template #inputicon="slotProps">
          <i
            class="pi pi-clock"
            @click="slotProps.clickCallback"
          />
        </template>
      </DatePicker>
      <small
        v-if="errors['end.time']"
        class="text-xs text-red-600"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.endTime.errorText"
        >{{ rawT(`veeValidate.messages.${errors["end.time"]}`) }}</small
      >
    </div>
    <div class="col-span-2 flex items-center gap-2">
      <ToggleSwitch
        v-model="indefinitePeriod"
        :data-test-id="DATA_TEST_IDS.components.programmingDateRangeInputs.indefinitePeriodSwitch"
        :disabled="props.disabled"
      />
      <span>{{ t(k.control.piloting.planning.form.indefinitePeriod) }}</span>
    </div>
  </div>
</template>
